import { useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout, TableCard } from "ui-components/Layout";
import { ActiveButton, EditButton, DelButton } from "ui-components/IconButton";
import { Success, Error_, MessageDialog } from "ui-components/Alert";
import { useCustomerValue } from "providers/CustomerContext";
import {
  getConsigneeList,
  getConsigneeStatusCount,
  deactivateConsignee,
  activeConsignee,
} from "services/data.internal.service";
import BasicTabs from "ui-components/BasicTabs";
import { useTeamValue } from "providers/TeamContext";
import { CSVLink } from "react-csv";
import dayjs from "dayjs";
import { ExportDialog } from "ui-components/Alert";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";

function ConsigneeList() {
  const { customerCode } = useCustomerValue();
  const { permissions, allowAll } = useTeamValue();
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const [data, setData] = useState([]);
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["consignee-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const { downloadCsv, isDownloading } = useCsvDownloadHook();

  const [filteredData, setFilteredData] = useState([]);

  useQuery({
    queryKey: ["consignee-list", { customerCode, active }],
    queryFn: () => getConsigneeList({ customerCode, active }),
    onSuccess: (data) => {
      setFilteredData(data.data.data);
      setData(data.data.data);
    },
    onError,
  });

  useQuery({
    queryKey: ["consignee-list", "status", { customerCode }],
    queryFn: () => getConsigneeStatusCount({ customerCode }),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deactivateConsignee, { onSuccess, onError });
  const activeMutation = useMutation(activeConsignee, { onSuccess, onError });

  const addPermission =
    allowAll ||
    (Boolean(permissions["Consignee"]) &&
      permissions["Consignee"].findIndex((b) => b === "new") !== -1);
  const editPermission =
    allowAll ||
    (Boolean(permissions["Consignee"]) &&
      permissions["Consignee"].findIndex((b) => b === "edit") !== -1);
  const deactivatePermission =
    allowAll ||
    (Boolean(permissions["Consignee"]) &&
      permissions["Consignee"].findIndex((b) => b === "deactivate") !== -1);

  const onNew = () => navigate("/setup/consignee/add");

  const onEdit = (req) => navigate("/setup/consignee/edit", { state: req });

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${req.consigneeCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate ${req.consigneeCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Consignee Code", accessorKey: "consigneeCode" },
    { header: "Customer Code", accessorKey: "customerCode" },
    { header: "Consignee Short Name", accessorKey: "consgShortName", width: "200px" },
    { header: "Contact Person", accessorKey: "contactPerson" },
    { header: "Mobile No", accessorKey: "contactMobileNo" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { consigneeCode, customerCode } = row.original;
        const state = { consigneeCode, customerCode };
        if (active) {
          return (
            <>
              <EditButton
                title="edit consignee"
                disabled={!editPermission}
                onClick={() => onEdit(state)}
              />
              <DelButton
                title="deactivate consignee"
                disabled={!deactivatePermission}
                onClick={() => onDelete(state)}
              />
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate consignee"
              disabled={!editPermission}
              onClick={() => onActive(state)}
            />
          );
        }
      },
    },
  ];
  const handleFilterRowsChange = (filteredData) => {
    setFilteredData(filteredData);
  };
  return (
    <ListLayout>
      {isDownloading && <ExportDialog />}
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" margin="10px">
          <MDBox>
            <MDButton variant="gradient" color="info" disabled={!addPermission} onClick={onNew}>
              <Icon>add</Icon>&nbsp;consignee
            </MDButton>
            <MDButton
              sx={{ marginLeft: "1rem" }}
              variant="outlined"
              color="dark"
              disabled={!addPermission}
              onClick={() => navigate("/setup/consignee/upload")}
            >
              <Icon>upload</Icon> &nbsp;upload
            </MDButton>
          </MDBox>
          {/* <CSVLink filename="ExportConsignee_" data={filteredData}>
            <MDButton variant="outlined" color="dark" title="export consignee">
              <Icon>download</Icon>&nbsp;export
            </MDButton>
          </CSVLink> */}
          <MDButton
            variant="outlined" color="dark" title="export consignee"
            onClick={() => {
              const filename = `${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_ExportConsignee`;
              downloadCsv(filename, filteredData)
            }}
          >
            <Icon>download</Icon>&nbsp;export
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={data} onFilterRowsChange={handleFilterRowsChange} />
      </TableCard>
    </ListLayout>
  );
}

export default ConsigneeList;
